import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCalendarAlt, FaFileExcel, FaFilePdf, FaSearch } from 'react-icons/fa';
import api from '../../api';
import YoungApprenticeService from '../../services/YoungApprenticeService';
import { downloadYoungApprenticeTablePdf } from './youngApprenticeReportPdf';

const dateBr = value => value ? String(value).slice(0, 10).split('-').reverse().join('/') : '—';
const currentMonth = () => {
  const now = new Date();
  const year = now.getFullYear(), month = now.getMonth();
  const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;
  return { dateFrom: `${prefix}-01`, dateTo: `${prefix}-${new Date(year, month + 1, 0).getDate()}` };
};
const commonColumns = [['apprenticeNumber', 'Nº aprendiz'], ['fullName', 'Nome'], ['cpf', 'CPF'], ['companyName', 'Empresa']];
const definitions = {
  VACATIONS: {
    title: 'Relatório de Férias', filename: 'ferias-aprendizes',
    description: 'Férias que coincidem com o período informado. Somente aprendizes e vínculos ativos.',
    columns: [...commonColumns, ['vacationStartDate', 'Início das férias'], ['vacationEndDate', 'Fim das férias'], ['vacationDays', 'Dias']],
  },
  CONTRACT_ENDS: {
    title: 'Relatório de Términos de Contratos', filename: 'terminos-contratos-aprendizes',
    description: 'Contratos com término no período informado. Somente aprendizes e vínculos ativos.',
    columns: [...commonColumns, ['startDate', 'Início do contrato'], ['endDate', 'Término do contrato'], ['role', 'Função']],
  },
};
const cellValue = (row, key) => key.endsWith('Date') ? dateBr(row[key]) : row[key] ?? '—';

export default function YoungApprenticePeriodReport({ reportType }) {
  const definition = definitions[reportType];
  const [period, setPeriod] = useState(currentMonth);
  const [appliedPeriod, setAppliedPeriod] = useState(null);
  const [rows, setRows] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);
  const load = useCallback(async next => {
    const requestId = ++request.current;
    setRows([]); setAppliedPeriod(null); setError('');
    if (!next.dateFrom || !next.dateTo || next.dateTo < next.dateFrom) {
      setLoading(false); setError('Informe um período válido, com data inicial e final.'); return;
    }
    setLoading(true);
    try {
      const result = await YoungApprenticeService.listApprenticeReport({ ...next, reportType, status: 'ACTIVE', studentStatus: 'ACTIVE' });
      if (requestId !== request.current) return;
      setRows(Array.isArray(result) ? result : []); setAppliedPeriod({ ...next });
    } catch (err) {
      if (requestId === request.current) setError(err?.response?.data?.error || 'Não foi possível carregar o relatório.');
    } finally { if (requestId === request.current) setLoading(false); }
  }, [reportType]);

  useEffect(() => {
    load(currentMonth());
    return () => { request.current += 1; };
  }, [load]);
  useEffect(() => {
    let active = true;
    api.get('/company').then(result => { if (active) setOrganization(result.data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const summary = appliedPeriod ? `${dateBr(appliedPeriod.dateFrom)} a ${dateBr(appliedPeriod.dateTo)} • Somente aprendizes e vínculos ativos` : '';
  const filename = `${definition.filename}-${appliedPeriod?.dateFrom}-${appliedPeriod?.dateTo}`;
  const pdf = async () => {
    setDownloading(true); setError('');
    try {
      await downloadYoungApprenticeTablePdf({
        title: definition.title, subtitle: 'Gestão de Aprendiz', organization, rows, filters: summary,
        columns: definition.columns.map(([key, label]) => ({ key, label, value: row => cellValue(row, key), weight: ['fullName', 'companyName'].includes(key) ? 1.5 : 1 })),
        filename: `${filename}.pdf`,
      });
    } catch (err) { setError(err?.message || 'Não foi possível gerar o PDF.'); }
    finally { setDownloading(false); }
  };
  const excel = () => {
    const escape = value => {
      const text = String(value);
      return `"${(/^[=+@-]/.test(text) ? `'${text}` : text).replace(/"/g, '""')}"`;
    };
    const csv = [definition.columns.map(([, label]) => label), ...rows.map(row => definition.columns.map(([key]) => cellValue(row, key)))]
      .map(line => line.map(escape).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a'); link.href = url; link.download = `${filename}.csv`; link.click(); URL.revokeObjectURL(url);
  };
  const exportDisabled = loading || downloading || !appliedPeriod || !rows.length
    || period.dateFrom !== appliedPeriod.dateFrom || period.dateTo !== appliedPeriod.dateTo;
  return <div className="young-apprentice-page ya-font-montserrat ya-clean-page">
    <header className="ya-clean-header ya-clean-header--navy"><div><span className="ya-page-toolbar__icon"><FaCalendarAlt /></span><h1>{definition.title}</h1></div><div className="ya-header-actions">
      <button type="button" className="young-apprentice-button young-apprentice-button--secondary" disabled={exportDisabled} onClick={excel}><FaFileExcel /> Excel</button>
      <button type="button" className="young-apprentice-button young-apprentice-button--primary" disabled={exportDisabled} onClick={pdf}><FaFilePdf /> {downloading ? 'Gerando...' : 'Baixar PDF'}</button>
    </div></header>
    <p className="ya-report-note">{definition.description}</p>
    <form className="young-apprentice-card ya-period-report-filters" onSubmit={event => { event.preventDefault(); load(period); }}>
      <label>Data inicial<input type="date" required value={period.dateFrom} onChange={event => setPeriod(current => ({ ...current, dateFrom: event.target.value }))} /></label>
      <label>Data final<input type="date" required value={period.dateTo} min={period.dateFrom} onChange={event => setPeriod(current => ({ ...current, dateTo: event.target.value }))} /></label>
      <button type="submit" className="ya-compact-btn ya-compact-btn--primary" disabled={loading}><FaSearch /> {loading ? 'Buscando...' : 'Buscar'}</button>
    </form>
    {error && <p role="alert" className="young-apprentice-feedback">{error}</p>}
    <section className="young-apprentice-card ya-table-card" aria-label="Resultados do relatório" aria-busy={loading}>
      <p role="status" className="ya-report-note">{loading ? 'Carregando relatório...' : `${rows.length} registro(s) encontrado(s). ${summary}`}</p>
      <div className="young-apprentice-table"><table><thead><tr>{definition.columns.map(([key, label]) => <th scope="col" key={key}>{label}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={`${row.employmentId}-${row.vacationId ?? index}`}>{definition.columns.map(([key]) => <td key={key}>{cellValue(row, key)}</td>)}</tr>)}
          {!loading && !rows.length && <tr><td colSpan={definition.columns.length}>Nenhum registro encontrado para o período.</td></tr>}
        </tbody></table></div>
    </section>
  </div>;
}
